import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import {
  ActionButtons,
  Container,
  DataTable,
  DateUtils,
  Navigation,
  Panel,
} from '../../components';
import { THEME } from '../../../constants/styles';
import { REQUESTING } from '../../../actions/statusTypes';
import {
  getCMRRevenueCycleServices,
  updateCMRRevenueCycleService,
} from '../cmr_revenue_cycle/slice';

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatEstimatedServiceValue(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? usdFormatter.format(numericValue) : value || '';
}

export default memo(function CMRRevenueCycle(props) {
  const dispatch = useDispatch();
  const employerId = props.employer?.employerId;
  const services = useSelector((state) => state.screens.cmrRevenueCycle.servicesData || []);
  const servicesStatus = useSelector((state) => state.screens.cmrRevenueCycle.servicesStatus);
  const servicesError = useSelector((state) => state.screens.cmrRevenueCycle.servicesError);
  const updateStatus = useSelector((state) => state.screens.cmrRevenueCycle.updateServiceStatus);
  const updateError = useSelector((state) => state.screens.cmrRevenueCycle.updateServiceError);

  const isLoading = servicesStatus === REQUESTING;
  const isMutating = updateStatus === REQUESTING;

  useEffect(() => {
    if (!employerId) return;

    dispatch(getCMRRevenueCycleServices({ employerId }));
  }, [dispatch, employerId]);

  const rowData = useMemo(() => {
    return services.map((service) => ({
      ...service,
      key: service.serviceId,
      estimatedServiceValueLabel: formatEstimatedServiceValue(service.estimatedServiceValue),
      dateUpdatedLabel: DateUtils.formatDateStringNumeric(service.dateUpdated),
    }));
  }, [services]);

  const openEditorModal = useCallback(
    (item) => {
      Navigation.showModal({
        screen: 'CMRRevenueCycleServiceForm',
        title: item.name,
        passProps: {
          employer: props.employer,
          item,
          canEdit: props.canEdit,
        },
        options: {
          containerStyle: {
            maxWidth: 700,
          },
        },
      });
    },
    [props.canEdit, props.employer]
  );

  const handleEditPress = useCallback(
    (item) => {
      openEditorModal(item);
    },
    [openEditorModal]
  );

  const handleToggleActive = useCallback(
    (item) => {
      dispatch(
        updateCMRRevenueCycleService({
          employerId,
          serviceId: item.serviceId,
          updates: { isActive: !item.isActive },
        })
      );
    },
    [dispatch, employerId]
  );

  const handleToggleActivePress = useCallback(
    (item) => {
      const nextIsActive = !item.isActive;
      const action = nextIsActive ? 'Reactivate' : 'Deactivate';

      props.onConfirmationOpts({
        title: `${action} this CMR revenue cycle service?`,
        description: `${item.name} will be ${nextIsActive ? 'available' : 'unavailable'} for CMR revenue tracking.`,
        confirm: `Yes, ${action}`,
        onConfirmed: () => {
          handleToggleActive(item);
        },
      });
    },
    [handleToggleActive, props.onConfirmationOpts]
  );

  const renderDimmedText = useCallback((item, accessor) => {
    return (
      <Text style={[THEME.rowText, !item.isActive ? styles.inactiveContent : null]}>
        {item[accessor]}
      </Text>
    );
  }, []);

  const columns = useMemo(() => {
    return [
      {
        headerLabel: 'ATTACHED SERVICE',
        style: { width: 320, flexGrow: 2 },
        dataAccessor: (item) => renderDimmedText(item, 'name'),
      },
      {
        headerLabel: 'ESTIMATED SERVICE VALUE',
        style: { width: 220, flexGrow: 1 },
        dataAccessor: (item) => renderDimmedText(item, 'estimatedServiceValueLabel'),
      },
      {
        headerLabel: 'DATE UPDATED',
        style: { width: 150, flexGrow: 0 },
        dataAccessor: (item) => renderDimmedText(item, 'dateUpdatedLabel'),
      },
      {
        headerLabel: 'ACTIONS',
        style: { width: 100, flexGrow: 0, flexShrink: 0 },
        dataAccessor: (item) => (
          <View>
            <ActionButtons
              item={item}
              onEditPress={handleEditPress}
              editDisabled={!props.canEdit || isMutating}
              onCancelPress={item.isActive ? handleToggleActivePress : null}
              cancelDisabled={!props.canEdit || isMutating}
              onAcceptPress={!item.isActive ? handleToggleActivePress : null}
              acceptDisabled={!props.canEdit || isMutating}
            />
          </View>
        ),
      },
    ];
  }, [handleEditPress, handleToggleActivePress, isMutating, props.canEdit, renderDimmedText]);

  if (!employerId) return null;

  return (
    <Panel title="CMR Revenue Cycle" containerStyle={[styles.panel, props.containerStyle]}>
      <Container
        errorMessage={updateError?.message || servicesError?.message || null}
        renderActivityOverlay={isLoading || isMutating}
      >
        <DataTable columns={columns} data={rowData} altColors={true} />
        {!isLoading && rowData.length === 0 && (
          <View style={styles.emptyStateContainer}>
            <Text style={THEME.textRegular}>No CMR revenue cycle services are available.</Text>
          </View>
        )}
      </Container>
    </Panel>
  );
});

const styles = StyleSheet.create({
  panel: {
    minWidth: 820,
  },
  inactiveContent: {
    opacity: 0.45,
  },
  emptyStateContainer: {
    paddingVertical: 25,
    paddingHorizontal: 15,
  },
});
